/**
 * MergeCapabilityNotice — statement-capability notice (EDG-01/02/03/04/29)
 * rendered INSIDE the MERGE canvas, above the target/source/ON cards, so the
 * dialect truth is visible wherever the MERGE is edited (not only in the
 * compact hand-off dialog).
 *
 * Two mutually-exclusive states:
 *  - Engine has no MERGE at all (mysql, sqlite) → loud amber banner naming
 *    the engine + "will fail". The PRIMARY next step is now
 *    "Convert to native upsert" (EDG-01/03) — it rewrites the MERGE in place
 *    as the engine's `INSERT … ON DUPLICATE KEY UPDATE` / `INSERT … ON CONFLICT
 *    DO UPDATE`, landing it in the real upsert editor. When the MERGE is
 *    outside the convertible subset, an honest per-reason line names the
 *    blocking construct and "Edit as SQL" stays the escape.
 *  - Engine's MERGE support is version-gated (postgres 15+) → a quiet
 *    version hint. Convert is offered here too — pg's native upsert
 *    (`ON CONFLICT`) expresses the same intent without needing v15.
 *
 * Deliberately does NOT touch editability or Execute: every WHEN-branch
 * control below keeps its existing § 1.4 disabled/enabled state (the user
 * may be drafting this MERGE for another engine), and Execute itself is
 * is the Query Editor toolbar's — outside this component's tree.
 */

import { AlertTriangle, Wand2 } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';

import { Button } from '../../../../../components/ui/button';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { ConvertMergeResult } from '../../edit/edits/convertMergeToUpsert';
import { ENGINE_DISPLAY_NAME, mergeMinVersionHint, supportsMerge } from '../../dialect/statementCapabilities';
import { vqeCopy } from '../../ux/vqeCopy';

export interface MergeCapabilityNoticeProps {
  readonly engine: SqlEngine;
  readonly onEditAsSql?: () => void;
  /**
   * Pure convertibility result for this MERGE. `null` when convert isn't
   * offered (supported engine, or no MERGE outline). `{ok:false}` carries the
   * honest blocking reason.
   */
  readonly convert?: ConvertMergeResult | null;
  /**
   * Runs the transform + range-replace apply. Present only when an edit
   * context is available (a Monaco model + resolver to write into). Resolves
   * on success, rejects if the buffer moved before apply.
   */
  readonly onConvert?: () => Promise<void>;
}

export function MergeCapabilityNotice({ engine, onEditAsSql, convert, onConvert }: MergeCapabilityNoticeProps) {
  const engineName = ENGINE_DISPLAY_NAME[engine];
  const { track } = useAnalytics();

  const handleConvert = useCallback(async () => {
    if (!onConvert) return;
    try {
      await onConvert();
      track('feature:vqe_merge_convert', { engine, outcome: 'applied' });
    } catch {
      // Apply failed (e.g. the buffer moved) — no success analytics; the
      // "Edit as SQL" escape stays available.
    }
  }, [onConvert, track, engine]);

  // Telemetry for encountering a MERGE we can't auto-convert (fires once per
  // mount, low-cardinality — no SQL text).
  const notConvertibleReason = convert && !convert.ok ? convert.reason : null;
  const firedRef = useRef(false);
  useEffect(() => {
    if (notConvertibleReason && !firedRef.current) {
      firedRef.current = true;
      track('feature:vqe_merge_convert', { engine, outcome: 'not-convertible' });
    }
  }, [notConvertibleReason, engine, track]);

  const canConvert = convert?.ok === true && !!onConvert;

  const convertButton = canConvert ? (
    <Button
      type="button"
      size="sm"
      onClick={() => void handleConvert()}
      data-testid="vqe-merge-convert-cta"
      className="shrink-0"
    >
      <Wand2 className="h-4 w-4" aria-hidden />
      {vqeCopy.capability.merge.convertCta}
    </Button>
  ) : null;

  const editAsSqlLink = onEditAsSql ? (
    <button
      type="button"
      onClick={onEditAsSql}
      data-testid="vqe-merge-capability-edit-as-sql"
      className="shrink-0 font-medium underline-offset-2 hover:underline"
    >
      {vqeCopy.actions.editAsSql}
    </button>
  ) : null;

  if (!supportsMerge(engine)) {
    return (
      <div
        role="alert"
        data-testid="vqe-merge-capability-banner"
        data-engine={engine}
        className="flex flex-col gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">{vqeCopy.capability.merge.unsupportedBanner(engineName)}</span>
        </div>
        {notConvertibleReason && (
          <p className="pl-6" data-testid="vqe-merge-convert-unavailable">
            {vqeCopy.capability.merge.notConvertible(notConvertibleReason)}
          </p>
        )}
        <div className="flex items-center gap-3 pl-6">
          {convertButton}
          {editAsSqlLink}
        </div>
        {engine === 'mysql' && canConvert && (
          <p className="pl-6 text-[11px]" data-testid="vqe-merge-convert-mysql-caveat">
            {vqeCopy.capability.merge.convertMysqlKeyCaveat}
          </p>
        )}
      </div>
    );
  }

  const minVersion = mergeMinVersionHint(engine);
  if (minVersion) {
    return (
      <div className="flex flex-col gap-2" data-testid="vqe-merge-version-hint">
        <p className="text-xs text-muted-foreground">
          {vqeCopy.capability.merge.versionHint(engineName, minVersion)}
        </p>
        {(convertButton || (notConvertibleReason && convert)) && (
          <div className="flex items-center gap-3">
            {convertButton}
            {notConvertibleReason && (
              <span className="text-xs text-muted-foreground" data-testid="vqe-merge-convert-unavailable">
                {vqeCopy.capability.merge.notConvertible(notConvertibleReason)}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
}
